import {
  EventStatus,
  EventType,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { TabsProps } from "antd";
import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import TabLabelField from "components/fields/TabLabelField";
import { namePathOrMatcher } from "utils/formUtils";

import DiscountCodes from "./Tabs/DiscountCodes";
import EventInfo from "./Tabs/EventInfo";
import Tickets from "./Tabs/Tickets";
import { UseEventTemplateFormSubmitButtons } from "./types";
import ConnectedEventList from "./Tabs/ConnectedEvents";
import { RECURRING_EVENT_TAB_NAMES } from "./consts";

export const useRecuringEventFormSubmitButtons: UseEventTemplateFormSubmitButtons =
  (form) => ({
    onPublish: useCallback(() => {
      form?.setFieldValue(["input", "status"], EventStatus.PUBLISHED);
    }, [form]),
    onDraft: useCallback(() => {
      form?.setFieldValue(["input", "status"], EventStatus.DRAFT);
    }, [form]),
  });

export const useEventFormTabs = (
  eventType: EventType,
  isRecurringEventCreate?: boolean,
  isRecurringEventDetails?: boolean,
  templateId?: number | undefined
): TabsProps["items"] => {
  const intl = useIntl();

  // eslint-disable-next-line no-restricted-globals
  const isRecurringEventCreate2 = location.pathname.includes("new");

  const baseTabs = useMemo<TabsProps["items"]>(
    () => [
      {
        disabled: false,
        label: (
          <TabLabelField
            label={intl.formatMessage({
              defaultMessage: "Event Info",
            })}
            matcher={namePathOrMatcher({
              equals: [
                ["input", "name"],
                ["input", "description"],
                ["input", "ticketDescription"],
                ["input", "timezone"],
                ["input", "date"],
                ["input", "doorTime"],
                ["input", "ageLimit"],
                ["input", "recurringSettings"],
                ["input", "publishAt"],
                ["input", "sendTicketsAt"],
                ["input", "youtubeUrl"],
                ["input", "image"],
                ["input", "seatMap"],
                ["input", "presalePasswords"],
              ],
            })}
          />
        ),
        key: RECURRING_EVENT_TAB_NAMES.details,
        children: <EventInfo />,
        forceRender: true,
      },
      ...(isRecurringEventCreate2
        ? [
            {
              disabled: false,
              label: (
                <TabLabelField
                  label={
                    eventType === EventType.NONTICKETED
                      ? intl.formatMessage({
                          defaultMessage: "Sign up options",
                        })
                      : intl.formatMessage({
                          defaultMessage: "Tickets",
                        })
                  }
                  matcher={namePathOrMatcher({
                    startsWith: [["input", "ticketPools"]],
                  })}
                />
              ),
              key: RECURRING_EVENT_TAB_NAMES.tickets,
              children: <Tickets {...{ eventType }} />,
              forceRender: true,
            },
          ]
        : []),
      ...(eventType === EventType.TICKETED
        ? [
            {
              disabled: false,
              label: (
                <TabLabelField
                  label={intl.formatMessage({
                    defaultMessage: "Discount Codes",
                  })}
                  matcher={namePathOrMatcher({
                    startsWith: [["input", "discountCodes"]],
                  })}
                />
              ),
              key: RECURRING_EVENT_TAB_NAMES.codes,
              children: <DiscountCodes />,
              forceRender: true,
            },
          ]
        : []),
    ],
    [intl, isRecurringEventCreate2, eventType]
  );

  const connectedEventsTab = useMemo(
    () =>
      templateId !== undefined
        ? {
            disabled: false,
            label: (
              <TabLabelField
                label={intl.formatMessage({
                  defaultMessage: "Connected events",
                })}
                matcher={namePathOrMatcher({
                  startsWith: [["input", "ticketPools"]],
                })}
              />
            ),
            key: RECURRING_EVENT_TAB_NAMES.events,
            children: <ConnectedEventList templateId={templateId} />,
            forceRender: true,
          }
        : undefined,
    [intl, templateId]
  );

  return [
    ...(baseTabs ?? []),
    ...(connectedEventsTab ? [connectedEventsTab] : []),
  ];
};
